import React from 'react'
import { graphql, Link } from 'gatsby'
import styled, { css } from 'styled-components'

import Navbar from '../../components/Navbar'
import Pill from '../../components/shared/Pill'
import RenderMarkdown from '../../components/RenderMarkdown'
import SEO from '../../components/SEO'
import SearchBar from '../../components/shared/SearchBar'
import TableOfContent from '../../components/shared/TableOfContent'
import {
  H1,
  H1WithCopyIcon,
  H2,
  H2WithCopyIcon,
  H3WithCopyIcon,
  H4WithCopyIcon,
  H5WithCopyIcon,
  P,
} from '../../components/shared/Typography'
import Footer from '../../components/Footer'

import useIsMobile from '../../hooks/useIsMobile'
import { OnlyOnDesktop, OnlyOnMobile } from '../../components/shared/Utils'

const Container = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  scroll-behavior: smooth;
  padding-bottom: 32px;

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.md}) {
    padding: 0 24px;
    padding-bottom: 48px;
  }

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.xl}) {
    padding: 0;
    padding-bottom: 48px;
  }
`

const IntroContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid ${(p) => p.theme.chameleon.colors.pusher.borderColor};

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.md}) {
    padding-top: 0;
    border-bottom: none;
  }
`

const Info = styled.aside`
  display: unset;
  padding: 24px 0;

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.md}) {
    border-top: none;
    border-bottom: none;
    padding-top: 0;
    max-width: 216px;
  }
  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.xl}) {
    max-width: 264px;
  }
`
const InfoContent = styled.div`
  margin: 16px 0;

  ${(p) =>
    p.$isSticky
      ? css`
          position: sticky;
          align-self: flex-start;
          top: 32px;
        `
      : ''}
`
const InfoHeading = styled(P)`
  font-weight: 600;
  line-height: 16px;
  margin-bottom: 0;

  &:first-of-type {
    margin-top: 0;
  }
`

const Toc = styled(InfoContent)`
  background-color: #ffffff;
  top: 0;
  padding: 16px 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.md}) {
    display: block;
    z-index: 1;
    padding: 0;
    top: 108px;
    max-width: 296px;
    margin-top: 64px;
  }
`

const Content = styled.div`
  width: 100%;
  overflow-x: hidden;
  border-top: 1px solid ${(p) => p.theme.chameleon.colors.pusher.borderColor};
  padding-top: 32px;
  max-width: 680px;

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.md}) {
    padding-top: 0;
    border-top: none;
  }

  p:first-of-type {
    margin-top: 0;
  }

  img {
    width: 100%;
  }
`

const Author = styled(Link)`
  display: flex;
  align-items: center;
  margin: 0;

  :hover {
    cursor: pointer;
    color: ${(p) => p.theme.chameleon.colors.pusher.darkPink};
    border-color: ${(p) => p.theme.chameleon.colors.pusher.darkPink};
  }
`

const ItemTags = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 16px;
  margin: 8px 0;
`

const ShareButton = styled.a`
  background-color: ${(p) => p.theme.chameleon.colors.pusher.darkPurple};
  color: #fff;
  display: block;
  padding: 8px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 0.25px;
  text-decoration: none;
`

const HiddenIntroductionHeading = styled(H2)`
  visibility: hidden;
  height: 0;
  scroll-margin-top: 156px;
`
const FeaturedImageComponent = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 3/2;
  object-fit: cover;
`

const MobileView = styled(OnlyOnMobile)`
  padding-top: 32px;
`

const DesktopView = styled(OnlyOnDesktop)`
  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.md}) {
    display: flex;
    gap: 24px;
    padding-top: 64px;
  }

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.lg}) {
    gap: 104px;
  }

  @media (min-width: ${(p) => p.theme.chameleon.breakpoints.xl}) {
    gap: 152px;
  }
`

const Blog = ({ data, location }) => {
  const post = data.strapiBlogPost
  const timeToRead = post.Body.data.childMarkdownRemark.timeToRead
  const postHeadings = post.Body.data.childMarkdownRemark.headings
  const postBody = post.Body.data.childMarkdownRemark.rawMarkdownBody
  const postFeaturedImage = post.FeaturedImage?.url
  const postTwitterFeaturedImage = post.TwitterFeaturedImage?.url
  const postLinkedInFeaturedImage = post.LinkedInFeaturedImage?.url
  const isMobile = useIsMobile()

  const getArticleUrl = () => {
    return typeof window !== 'undefined' ? window.location.href : location.href
  }

  const Sidebar = (
    <Info>
      <InfoContent>
        <InfoHeading>Authors</InfoHeading>
        {post.Authors.map(({ Slug, Name, Role }) => (
          <Author key={Slug} to={`/author/${Slug}`}>
            {Name} {Role && `(${Role})`}
          </Author>
        ))}
      </InfoContent>

      <InfoContent>
        <InfoContent>
          <InfoHeading>{timeToRead} minute read</InfoHeading>
        </InfoContent>

        {post.DatePublished === post.DateLastUpdated ? (
          <InfoContent>
            <InfoHeading>Published</InfoHeading>

            {post.DatePublished}
          </InfoContent>
        ) : (
          <InfoContent>
            <InfoHeading>Last updated</InfoHeading>
            {post.DateLastUpdated}
            {` (published ${post.DatePublished})`}
          </InfoContent>
        )}
      </InfoContent>

      <InfoContent>
        <InfoHeading>Categories</InfoHeading>
        <ItemTags>
          {post.Categories.map(({ Slug, Name }) => (
            <Pill key={Slug} $isInverted={true}>
              {Name}
            </Pill>
          ))}
        </ItemTags>
      </InfoContent>

      {post.Tags?.length > 0 && (
        <InfoContent>
          <InfoHeading>Tags</InfoHeading>
          <ItemTags>
            {post.Tags.map(({ Slug, Name }) => (
              <Pill key={Slug} $isInverted={true}>
                {Name}
              </Pill>
            ))}
          </ItemTags>
        </InfoContent>
      )}

      <InfoContent>
        <InfoHeading>Share</InfoHeading>

        <ItemTags>
          <ShareButton
            href={`https://twitter.com/share?text=Check out this blog post on Pusher&url=${getArticleUrl()}`}
            target="__blank"
            rel="nofollow"
          >
            Share on Twitter
          </ShareButton>
          <ShareButton href={`https://discord.gg/K3svTwjRqD`} target="__blank">
            Discuss on Discord
          </ShareButton>
        </ItemTags>
      </InfoContent>

      <Toc $isSticky={true}>
        <TableOfContent headings={postHeadings} />
      </Toc>
    </Info>
  )

  const Intro = (
    <IntroContainer>
      <H1>{post.Title}</H1>
      {postFeaturedImage && <FeaturedImageComponent src={postFeaturedImage} alt={post.FeaturedImage.name} />}

      <OnlyOnMobile>
        <P>{post.Excerpt || post.MetaDescription}</P>
      </OnlyOnMobile>
    </IntroContainer>
  )

  return (
    <>
      <SEO
        title={`${post.Title} | Pusher blog`}
        description={post.Excerpt || post.MetaDescription}
        image={postFeaturedImage}
        twitterImage={postTwitterFeaturedImage}
        linkedInImage={postLinkedInFeaturedImage}
      />

      <Navbar isSticky={false} />
      <SearchBar title="Blog" location={location} />

      <Container>
        {isMobile && (
          <MobileView>
            {Intro}

            {Sidebar}

            <Content>
              <HiddenIntroductionHeading id="introduction">Introduction</HiddenIntroductionHeading>
              <RenderMarkdown
                components={{
                  h1: H1WithCopyIcon,
                  h2: H2WithCopyIcon,
                  h3: H3WithCopyIcon,
                  h4: H4WithCopyIcon,
                  h5: H5WithCopyIcon,
                }}
                markdown={postBody}
              />
            </Content>
          </MobileView>
        )}

        {!isMobile && (
          <DesktopView>
            {Sidebar}

            <Content>
              {Intro}

              <HiddenIntroductionHeading id="introduction">Introduction</HiddenIntroductionHeading>
              <RenderMarkdown
                components={{
                  h1: H1WithCopyIcon,
                  h2: H2WithCopyIcon,
                  h3: H3WithCopyIcon,
                  h4: H4WithCopyIcon,
                  h5: H5WithCopyIcon,
                }}
                markdown={postBody}
              />
            </Content>
          </DesktopView>
        )}
      </Container>

      <Footer />
    </>
  )
}

export const query = graphql`
  query ($Slug: String) {
    strapiBlogPost(Slug: { eq: $Slug }) {
      Title
      Slug
      Excerpt
      MetaTitle
      MetaDescription
      DatePublished(formatString: "Do MMMM YYYY")
      DateLastUpdated(formatString: "Do MMMM YYYY")
      Authors {
        Name
        Role
        Slug
      }
      Categories {
        Name
        Slug
      }
      Tags {
        Name
        Slug
      }
      FeaturedImage {
        url
        name
      }
      TwitterFeaturedImage {
        url
        name
      }
      LinkedInFeaturedImage {
        url
        name
      }
      Body {
        data {
          childMarkdownRemark {
            headings {
              value
              depth
            }
            timeToRead
            rawMarkdownBody
          }
        }
      }
    }
  }
`

export default Blog
